/** The value actions of the Limit hits page — one family, one links module (the detail-page value
 *  actions rule). Every href comes from `gridLinks`; `mcpLimitLinks.test.ts` proves each one lands
 *  on a real route and that the access-log search parses under that grid's namespaces. */
import type { McpLimitHitRow } from '@/api/mcpLimitHits';
import type { GridLink } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { cond, externalAccessHref, mcpLimitHitsHref, rangeCond } from '@/utils/gridLinks';

/** A tenant on the page: "Limit hits of this operator". */
export const tenantGrid = (tenantId: number): GridLink => ({
  href: mcpLimitHitsHref({ tenant: tenantId }),
  label: 'Limit hits of this operator',
});

/** A limit: only its rows, its key to copy (the settings name it by key). */
export function limitActions(key: string): ValueAction[] {
  return [
    act.filter(mcpLimitHitsHref({ limit: key }), 'Only this limit'),
    act.copy(key, 'Copy limit key'),
  ];
}

/** A rate limit's hits are access-log rows: the throttled MCP calls of that tenant, limit and UTC
 *  day on /external-access. A spend ceiling or a cap is counted from its audit event, which has no
 *  console grid: no link. */
export function accessLogHref(row: McpLimitHitRow): string | null {
  if (row.source !== 'access_log') return null;
  return externalAccessHref(
    [
      cond('channel', 'mcp'),
      cond('outcome', 'throttled'),
      cond('error_code', `limit_reached:${row.limit}`),
      rangeCond('created', `${row.day} 00:00`, `${row.day} 23:59`),
    ],
    { tenant: row.tenant_id },
  );
}

export function rowActions(row: McpLimitHitRow): ValueAction[] {
  const href = accessLogHref(row);
  return href ? [act.filter(href, 'The throttled MCP calls of this day')] : [];
}
